import type { Charge, ChargePopulateField } from "@investhinks/api-client";
import moment from "moment";

import { chargesEndpoints } from "../../api";
import { getCurrencyDisplay } from "../utils/currency";

// ============================================================================
// Types
// ============================================================================

/** Charge statuses that the monthly earnings graph counts as "pending" (unpaid). */
export const UNPAID_STATUSES = ["failed", "pending", "processing"] as const;
export type UnpaidStatus = (typeof UNPAID_STATUSES)[number];

export const POPULATE: ChargePopulateField[] = [
  "pm_user",
  "pm_payments",
  "pm_subscription.pm_paymentMethod",
  "pm_subscription.strategy",
  "bot_config.exchange_account",
];

export interface PaymentAttempt {
  id?: number | string;
  pm_status?: string | null;
  pm_failureReason?: string | null;
  pm_bank_message?: string | null;
  pm_risk_level?: string | null;
  createdAt?: string | null;
}

export interface CardDetails {
  brand?: string | null;
  last4?: string | null;
  exp_month?: string | null;
  exp_year?: string | null;
}

export interface SubscriptionInfo {
  id?: number | string;
  pm_status?: string | null;
  pm_failureReason?: string | null;
  pm_failureCount?: number | null;
  pm_lastRetryAt?: string | null;
  pm_adminAttentionNeeded?: boolean | null;
  pm_paymentMethod?: { pm_isValid?: boolean | null; pm_details?: CardDetails | null } | null;
  strategy?: { name?: string | null } | null;
}

/** Loosened view of a charge as returned with the populate above. */
export type UnpaidCharge = Charge & {
  pm_user?: { id?: number | string; name?: string | null; email?: string | null } | null;
  pm_payments?: PaymentAttempt[] | null;
  pm_subscription?: SubscriptionInfo | null;
  bot_config?: { exchange_account?: { name?: string | null } | null } | null;
};

export interface ChargeSummary {
  charge: UnpaidCharge;
  status: UnpaidStatus;
  amount: number;
  currency: string;
  reasonCode: string | null;
  bankMessage: string | null;
  attempts: number;
  lastAttemptAt: string | null;
  isManual: boolean;
}

export interface UserGroup {
  userId: number | string;
  name: string;
  email: string;
  charges: ChargeSummary[];
  unpaidByCurrency: Record<string, number>;
  unpaidEur: number;
  failedCount: number;
  attempts: number;
  lastFailureAt: string | null;
  latestReasonCode: string | null;
  latestBankMessage: string | null;
  subscription: SubscriptionInfo | null;
  hasValidCard: boolean;
  isCardExpired: boolean;
  isEscalated: boolean;
  needsAttention: boolean;
}

export type FilterKey = "all" | UnpaidStatus | "escalated" | "noPaymentMethod";

// ============================================================================
// Helpers
// ============================================================================

export const isUnpaidStatus = (status: string | null | undefined): status is UnpaidStatus =>
  !!status && (UNPAID_STATUSES as readonly string[]).includes(status);

export const lastFailedAttempt = (payments: PaymentAttempt[]): PaymentAttempt | undefined =>
  [...payments]
    .filter((p) => p.pm_status === "failed" || p.pm_status === "declined")
    .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())[0];

export const summarizeCharge = (charge: UnpaidCharge): ChargeSummary | null => {
  if (!isUnpaidStatus(charge.pm_status)) return null;
  const payments = charge.pm_payments ?? [];
  const failedAttempt = lastFailedAttempt(payments);
  const lastAttempt = [...payments].sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())[0];

  return {
    charge,
    status: charge.pm_status,
    amount: charge.pm_amount ?? 0,
    currency: charge.pm_currency ?? "EUR",
    reasonCode: charge.pm_failureReason || failedAttempt?.pm_failureReason || null,
    bankMessage: charge.pm_bank_message || failedAttempt?.pm_bank_message || null,
    attempts: payments.length,
    lastAttemptAt: lastAttempt?.createdAt ?? failedAttempt?.createdAt ?? null,
    isManual: charge.paymentType === "manual",
  };
};

export const isCardExpired = (details: CardDetails | null | undefined): boolean => {
  if (!details?.exp_month || !details?.exp_year) return false;
  const year = Number(details.exp_year.length === 2 ? `20${details.exp_year}` : details.exp_year);
  const month = Number(details.exp_month);
  if (!year || !month) return false;
  return moment({ year, month: month - 1 }).endOf("month").isBefore(moment());
};

export const groupByUser = (charges: UnpaidCharge[]): UserGroup[] => {
  const groups = new Map<string, UserGroup>();
  const seenChargeIds = new Set<string>();

  for (const charge of charges) {
    // Offset pagination over live data can return the same charge twice;
    // counting it twice would overstate the unpaid totals.
    const chargeKey = String(charge.id);
    if (seenChargeIds.has(chargeKey)) continue;
    seenChargeIds.add(chargeKey);

    const summary = summarizeCharge(charge);
    if (!summary) continue;

    const user = charge.pm_user;
    const key = String(user?.id ?? `unknown-${charge.id}`);
    let group = groups.get(key);
    if (!group) {
      group = {
        userId: user?.id ?? key,
        name: user?.name || user?.email || "—",
        email: user?.email || "",
        charges: [],
        unpaidByCurrency: {},
        unpaidEur: 0,
        failedCount: 0,
        attempts: 0,
        lastFailureAt: null,
        latestReasonCode: null,
        latestBankMessage: null,
        subscription: null,
        hasValidCard: false,
        isCardExpired: false,
        isEscalated: false,
        needsAttention: false,
      };
      groups.set(key, group);
    }

    group.charges.push(summary);
    group.unpaidByCurrency[summary.currency] = (group.unpaidByCurrency[summary.currency] ?? 0) + summary.amount;
    if (summary.currency === "EUR") group.unpaidEur += summary.amount;
    if (summary.status === "failed") group.failedCount += 1;
    group.attempts += summary.attempts;

    const failureAt = summary.status === "failed" ? (summary.lastAttemptAt ?? charge.updatedAt ?? null) : null;
    if (failureAt && (!group.lastFailureAt || moment(failureAt).isAfter(group.lastFailureAt))) {
      group.lastFailureAt = failureAt;
      group.latestReasonCode = summary.reasonCode;
      group.latestBankMessage = summary.bankMessage;
    }
    if (!group.latestReasonCode && summary.reasonCode) {
      group.latestReasonCode = summary.reasonCode;
      group.latestBankMessage = summary.bankMessage;
    }

    const sub = charge.pm_subscription ?? null;
    if (sub) {
      // Prefer the subscription with the most severe status
      const severity = (s?: string | null) =>
        s === "failed_escalated" ? 3 : s === "unpaid" ? 2 : s === "failed" ? 1 : 0;
      if (!group.subscription || severity(sub.pm_status) > severity(group.subscription.pm_status)) {
        group.subscription = sub;
      }
      const pmDetails = sub.pm_paymentMethod?.pm_details ?? null;
      if (sub.pm_paymentMethod?.pm_isValid) group.hasValidCard = true;
      if (isCardExpired(pmDetails)) group.isCardExpired = true;
      if (sub.pm_status === "failed_escalated") group.isEscalated = true;
      if (sub.pm_adminAttentionNeeded) group.needsAttention = true;
    }
  }

  const result = [...groups.values()];
  for (const g of result) {
    g.charges.sort(
      (a, b) => moment(b.charge.pm_periodEnd).valueOf() - moment(a.charge.pm_periodEnd).valueOf()
    );
  }
  // Newest failure first; users without a recorded failure go last, ordered by unpaid amount
  result.sort((a, b) => {
    const ta = a.lastFailureAt ? moment(a.lastFailureAt).valueOf() : 0;
    const tb = b.lastFailureAt ? moment(b.lastFailureAt).valueOf() : 0;
    if (ta !== tb) return tb - ta;
    return b.unpaidEur - a.unpaidEur;
  });
  return result;
};

export const PAGE_SIZE = 100;

/** Pages fetched at once after the first page reveals the page count. */
const MAX_CONCURRENT_PAGES = 4;

/** Guards against an unbounded sweep if the data keeps growing underneath us. */
const MAX_PAGES = 60;

const fetchUnpaidPage = (page: number) =>
  chargesEndpoints.getCharges({
    filters: { pm_status: { $in: [...UNPAID_STATUSES] } },
    pagination: { page, pageSize: PAGE_SIZE },
    // Ordered by a stable, immutable key. Sorting by `createdAt:desc` meant a
    // charge created mid-sweep shifted every later page down, so a boundary
    // charge could be returned on two pages or skipped entirely.
    sort: ["id:asc"],
    populate: POPULATE,
  });

/**
 * Loads every unpaid charge.
 *
 * Page 1 reveals the page count and the rest are fetched in bounded parallel
 * batches; walking them strictly one after another made this the slowest
 * request on the admin overview, and it gates the "Needs attention" panel.
 *
 * The page count is re-read as pages come back, because new failed charges
 * can push the total past the count page 1 reported.
 */
export const loadAllUnpaidCharges = async (): Promise<UnpaidCharge[]> => {
  const first = await fetchUnpaidPage(1);
  const all: UnpaidCharge[] = [...(first.data as unknown as UnpaidCharge[])];
  let pageCount = Math.min(first.meta?.pagination?.pageCount ?? 1, MAX_PAGES);

  let nextPage = 2;
  while (nextPage <= pageCount) {
    const batch: number[] = [];
    for (let i = 0; i < MAX_CONCURRENT_PAGES && nextPage + i <= pageCount; i++) batch.push(nextPage + i);
    const results = await Promise.all(batch.map(fetchUnpaidPage));
    for (const { data, meta } of results) {
      all.push(...(data as unknown as UnpaidCharge[]));
      const reported = meta?.pagination?.pageCount;
      if (reported && reported > pageCount) pageCount = Math.min(reported, MAX_PAGES);
    }
    nextPage += batch.length;
  }

  const reportedPages = first.meta?.pagination?.pageCount ?? 1;
  if (reportedPages > MAX_PAGES) {
    // Every figure derived from this list under-reports past the cap, so say so
    // rather than returning a silent prefix.
    console.warn(
      `Unpaid charges truncated at ${MAX_PAGES} pages of ${PAGE_SIZE}; the API reports ${reportedPages}.`
    );
  }
  return all;
};

export const formatMoney = (amount: number, currency: string) => `${amount.toFixed(2)} ${getCurrencyDisplay(currency)}`;

export const formatUnpaid = (byCurrency: Record<string, number>) =>
  Object.entries(byCurrency)
    .map(([c, v]) => formatMoney(v, c))
    .join(" + ");

